import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  type RouterHistory,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { Shell } from "../shell/Shell";
import type { PaneSpec } from "../workspace/paneTypes";
import { useWorkspace } from "../workspace/WorkspaceContext";

/** A route's only job is to make sure its pane is open. The workspace owns everything else. */
function RoutePane(props: { spec: PaneSpec }) {
  const ws = useWorkspace();
  const key = JSON.stringify(props.spec);
  useEffect(() => {
    ws.restoreRoute(JSON.parse(key) as PaneSpec);
    // `ws` changes identity on every tree change; keying on the spec keeps this to route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
}

const rootRoute = createRootRoute({ component: Shell });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/today", replace: true });
  },
});

const simple = <P extends string>(path: P, spec: PaneSpec) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: () => <RoutePane spec={spec} />,
  });

const todayRoute = simple("/today", { paneType: "today" });
const anytimeRoute = simple("/anytime", { paneType: "anytime" });
const inboxRoute = simple("/inbox", { paneType: "inbox" });
const upcomingRoute = simple("/upcoming", { paneType: "upcoming" });
const somedayRoute = simple("/someday", { paneType: "someday" });
const completedRoute = simple("/completed", { paneType: "completed" });
const notesRoute = simple("/notes", { paneType: "allNotes" });
const dailyRoute = simple("/daily", { paneType: "daily" });
const templatesRoute = simple("/templates", { paneType: "templates" });
const calendarRoute = simple("/calendar", { paneType: "calendar" });
const dashboardsRoute = simple("/dashboards", { paneType: "dashboards" });
const trashRoute = simple("/trash", { paneType: "trash" });
const settingsRoute = simple("/settings", { paneType: "settings" });

const noteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notes/$noteId",
  component: function NoteRoute() {
    const { noteId } = noteRoute.useParams();
    return <RoutePane spec={{ paneType: "note", noteId }} />;
  },
});

const taskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks/$taskId",
  component: function TaskRoute() {
    const { taskId } = taskRoute.useParams();
    return <RoutePane spec={{ paneType: "task", taskId }} />;
  },
});

const folderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/folders/$folderId",
  component: function FolderRoute() {
    const { folderId } = folderRoute.useParams();
    return <RoutePane spec={{ paneType: "folder", folderId }} />;
  },
});

const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId",
  component: function ProjectRoute() {
    const { projectId } = projectRoute.useParams();
    return <RoutePane spec={{ paneType: "project", projectId }} />;
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  todayRoute,
  inboxRoute,
  anytimeRoute,
  upcomingRoute,
  somedayRoute,
  completedRoute,
  notesRoute,
  dailyRoute,
  templatesRoute,
  calendarRoute,
  dashboardsRoute,
  folderRoute,
  trashRoute,
  settingsRoute,
  noteRoute,
  taskRoute,
  projectRoute,
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    ...(history ? { history } : {}),
    defaultPreload: false,
    scrollRestoration: false,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
